import { motion } from 'framer-motion';
import { useState } from 'react';
import { ExternalLink, Crown, Sparkles, Check, MessageCircle, AlertTriangle, Gem, PawPrint, ShoppingCart } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Section, SectionHeader } from '@/components/ui/Section';
import { Button } from '@/components/ui/button';
import { storeConfig, mountsConfig, siteConfig } from '@/config/site';
import { cn } from '@/lib/utils';
import { UsernameModal } from '@/components/payment/UsernameModal';
import { RulesModal } from '@/components/payment/RulesModal';
import { ServerAlertModal } from '@/components/payment/ServerAlertModal';
import { PaymentQRModal } from '@/components/payment/PaymentQRModal';
import { ReceiptModal } from '@/components/payment/ReceiptModal';
import { MountAlertModal } from '@/components/payment/MountAlertModal';

function RankCard({ rank, featured = false, onPurchase }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className={cn(
        'relative flex flex-col p-6 rounded-xl border bg-card/50 backdrop-blur-sm transition-all',
        featured
          ? 'border-primary/50 shadow-glow'
          : 'border-border hover:border-primary/30'
      )}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          Popular
        </div>
      )}

      {/* Rank Header */}
      <div className="mb-4">
        <div
          className={cn(
            'inline-flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-semibold',
            rank.color,
            'text-primary-foreground'
          )}
        >
          <Crown className="h-4 w-4" />
          {rank.name}
        </div>
        {rank.subtitle && (
          <p className="text-xs text-muted-foreground mt-2 italic">{rank.subtitle}</p>
        )}
      </div>

      {/* Price */}
      <div className="mb-6">
        <span className="font-display text-2xl md:text-3xl font-bold text-foreground">
          {rank.price}
        </span>
      </div>

      {/* Benefits */}
      <ul className="space-y-2.5 mb-6 flex-grow">
        {rank.benefits.map((benefit, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">{benefit}</span>
          </li>
        ))}
      </ul>

      {/* Purchase Button */}
      <Button
        onClick={onPurchase}
        className={cn(
          'w-full font-semibold mt-auto',
          featured
            ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
            : 'bg-secondary hover:bg-secondary/80 text-secondary-foreground'
        )}
      >
        <ShoppingCart className="h-4 w-4 mr-2" />
        Purchase
      </Button>
    </motion.div>
  );
}

function MountCard({ mount, featured = false, onPurchase }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className={cn(
        'relative flex flex-col p-6 rounded-xl border bg-card/50 backdrop-blur-sm transition-all',
        featured
          ? 'border-primary/50 shadow-glow'
          : 'border-border hover:border-primary/30'
      )}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          Popular
        </div>
      )}

      {/* Mount Image */}
      <div className="mb-4 aspect-video relative overflow-hidden rounded-lg bg-muted">
        <img
          src={mount.image}
          alt={mount.name}
          className="w-full h-full object-contain"
        />
      </div>

      {/* Mount Header */}
      <div className="mb-3">
        <h3 className="font-display text-xl font-bold text-foreground flex items-center gap-2">
          <PawPrint className="h-5 w-5 text-primary" />
          {mount.name}
        </h3>
      </div>

      {/* Price */}
      <div className="mb-4">
        <span className="font-display text-2xl font-bold text-primary">
          {mount.price}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        {mount.description}
      </p>

      {/* Stats */}
      <div className="mb-6 p-3 rounded-lg bg-muted/50">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Speed:</span>
            <span className="font-semibold">{mount.stats.speed}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Stamina:</span>
            <span className="font-semibold">{mount.stats.stamina}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Style:</span>
            <span className="font-semibold">{mount.stats.style}</span>
          </div>
        </div>
      </div>

      {/* Purchase Button */}
      <Button
        onClick={onPurchase}
        className={cn(
          'w-full font-semibold mt-auto',
          featured
            ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
            : 'bg-secondary hover:bg-secondary/80 text-secondary-foreground'
        )}
      >
        <ShoppingCart className="h-4 w-4 mr-2" />
        Purchase
      </Button>
    </motion.div>
  );
}

function MountCategory({ category, index, onPurchaseClick }) {
  if (category.comingSoon) {
    return (
      <div className="mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center gap-4 mb-8"
        >
          <div
            className={cn(
              'h-1 flex-grow rounded-full bg-gradient-to-r',
              category.color
            )}
          />
          <h2 className="font-display text-2xl md:text-3xl font-bold whitespace-nowrap">
            {category.rarity}
          </h2>
          <div
            className={cn(
              'h-1 flex-grow rounded-full bg-gradient-to-l',
              category.color
            )}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center py-12"
        >
          <Sparkles className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-xl font-semibold text-muted-foreground">
            Coming Soon...
          </p>
          <p className="text-sm text-muted-foreground/70 mt-2">
            Epic mounts are being prepared for release!
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="mb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex items-center gap-4 mb-8"
      >
        <div
          className={cn(
            'h-1 flex-grow rounded-full bg-gradient-to-r',
            category.color
          )}
        />
        <h2 className="font-display text-2xl md:text-3xl font-bold whitespace-nowrap">
          {category.rarity}
        </h2>
        <div
          className={cn(
            'h-1 flex-grow rounded-full bg-gradient-to-l',
            category.color
          )}
        />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {category.mounts.map((mount, mountIndex) => (
          <MountCard
            key={mount.name}
            mount={mount}
            featured={index === 0 && mountIndex === 0}
            onPurchase={() => onPurchaseClick(mount.name, mount.price, 'mount', 'mount')}
          />
        ))}
      </div>
    </div>
  );
}

function TierSection({ tier, index, onPurchaseClick }) {
  return (
    <div className="mb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex items-center gap-4 mb-8"
      >
        <div
          className={cn(
            'h-1 flex-grow rounded-full bg-gradient-to-r',
            tier.color
          )}
        />
        <h3
          className={cn(
            'font-display text-lg md:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r shrink-0',
            tier.color
          )}
        >
          {tier.tier}
        </h3>
        <div
          className={cn(
            'h-1 flex-grow rounded-full bg-gradient-to-l',
            tier.color
          )}
        />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tier.ranks.map((rank, rankIndex) => (
          <RankCard
            key={rank.name}
            rank={rank}
            featured={index === 0 && rankIndex === 0}
            onPurchase={() => onPurchaseClick(rank.name, rank.price, rank.name.toLowerCase())}
          />
        ))}
      </div>
    </div>
  );
}

export default function StorePage() {
  const [activeTab, setActiveTab] = useState('ranks');
  
  // Payment flow states
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showServerAlert, setShowServerAlert] = useState(false);
  const [showMountAlert, setShowMountAlert] = useState(false);
  const [showPaymentQR, setShowPaymentQR] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  
  // Payment data states
  const [currentProduct, setCurrentProduct] = useState(null);
  const [username, setUsername] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [receiptData, setReceiptData] = useState(null);

  // Helper function to extract price number from string (e.g., "IDR 1.000.000" -> 1000000)
  const extractPrice = (priceString) => {
    return parseInt(priceString.replace(/[^0-9]/g, ''));
  };

  // Handle purchase button click
  const handlePurchaseClick = (productName, priceString, group, purchaseType = 'rank') => {
    setCurrentProduct({
      name: productName,
      price: priceString,
      priceNumber: extractPrice(priceString),
      group: group,
      purchaseType: purchaseType,
    });
    setShowUsernameModal(true);
  };

  // Handle username submission
  const handleUsernameSubmit = (submittedUsername) => {
    setUsername(submittedUsername);
    setShowUsernameModal(false);
    setShowRulesModal(true);
  };

  // Handle rules acceptance
  const handleRulesAccept = () => {
    setShowRulesModal(false);
    if (currentProduct?.purchaseType === 'mount') {
      setShowMountAlert(true);
    } else {
      setShowServerAlert(true);
    }
  };

  // Handle server alert continue
  const handleServerAlertContinue = () => {
    setShowServerAlert(false);
    
    // Prepare order data for QR Modal
    setOrderData({
      productName: currentProduct.name,
      amount: currentProduct.priceNumber,
      username: username,
      group: currentProduct.group,
      purchase_type: currentProduct.purchaseType || 'rank',
    });
    
    setShowPaymentQR(true);
  };

  // Handle mount alert continue
  const handleMountAlertContinue = () => {
    setShowMountAlert(false);
    
    // Prepare order data for QR Modal
    setOrderData({
      productName: currentProduct.name,
      amount: currentProduct.priceNumber,
      username: username,
      group: currentProduct.group,
      purchase_type: 'mount',
    });
    
    setShowPaymentQR(true);
  };

  // Handle payment success
  const handlePaymentSuccess = (receipt) => {
    setReceiptData(receipt);
    setShowPaymentQR(false);
    setShowReceipt(true);
  };

  // Handle payment failed
  const handlePaymentFailed = (error) => {
    setShowPaymentQR(false);
    alert('Pembayaran gagal: ' + error);
  };

  // Handle receipt close (reset all states)
  const handleReceiptClose = () => {
    setShowReceipt(false);
    setCurrentProduct(null);
    setUsername('');
    setOrderData(null);
    setReceiptData(null);
  };

  // Handle modal close (cancel payment flow)
  const handleCancelPayment = () => {
    setShowUsernameModal(false);
    setShowRulesModal(false);
    setShowServerAlert(false);
    setShowMountAlert(false);
    setShowPaymentQR(false);
    setCurrentProduct(null);
    setUsername('');
    setOrderData(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        <Section>
          <SectionHeader
            subtitle="Server Store"
            title={activeTab === 'ranks' ? "Rank Price List & Benefits" : "Mounts Collection"}
          />

          {/* Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center gap-4 mb-12"
          >
            <Button
              onClick={() => setActiveTab('ranks')}
              className={cn(
                'px-8 py-6 text-lg font-semibold transition-all',
                activeTab === 'ranks'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              )}
            >
              <Crown className="h-5 w-5 mr-2" />
              Ranks
            </Button>
            <Button
              onClick={() => setActiveTab('mounts')}
              className={cn(
                'px-8 py-6 text-lg font-semibold transition-all',
                activeTab === 'mounts'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              )}
            >
              <PawPrint className="h-5 w-5 mr-2" />
              Mounts
            </Button>
          </motion.div>

          {/* Store Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto mb-8 p-6 rounded-xl border border-primary/30 bg-primary/5"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                {activeTab === 'ranks' ? (
                  <Gem className="h-6 w-6 text-primary" />
                ) : (
                  <PawPrint className="h-6 w-6 text-primary" />
                )}
              </div>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {activeTab === 'ranks' ? storeConfig.description : mountsConfig.description}
              </p>
            </div>
          </motion.div>

          {/* Purchase Info - Only for Ranks */}
          {activeTab === 'ranks' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-4xl mx-auto mb-12 p-6 rounded-xl border border-border bg-card/50"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <MessageCircle className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-foreground mb-2">
                  Cara Pembelian
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Untuk membeli rank, silakan buka ticket di Discord server kami.
                  Staff akan membantu proses pembelian kamu.
                </p>
                <Button
                  asChild
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a
                    href={siteConfig.links.discord}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                  >
                    Open Ticket di Discord
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>
          )}

          {/* Render Ranks or Mounts based on active tab */}
          {activeTab === 'ranks' ? (
            <>
              {/* Rank Tiers */}
              {storeConfig.tiers.map((tier, index) => (
                <TierSection key={tier.tier} tier={tier} index={index} onPurchaseClick={handlePurchaseClick} />
              ))}

              {/* Important Note */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="max-w-4xl mx-auto mt-8 p-6 rounded-xl border border-yellow-500/30 bg-yellow-500/5"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center shrink-0">
                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <h4 className="font-display font-semibold text-foreground mb-1">
                      Informasi Penting
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {storeConfig.importantNote}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Footer Note */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-center mt-8 text-sm text-muted-foreground"
              >
                <p>
                  Semua rank bersifat permanen dan tidak akan expired.
                  <br />
                  Pembayaran dilakukan melalui Discord ticket.
                </p>
              </motion.div>
            </>
          ) : (
            <>
              {/* Mount Categories */}
              {mountsConfig.categories.map((category, index) => (
                <MountCategory key={category.rarity} category={category} index={index} onPurchaseClick={handlePurchaseClick} />
              ))}

              {/* Mounts Purchase Info */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="max-w-4xl mx-auto mt-8 p-6 rounded-xl border border-primary/30 bg-card/50"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <MessageCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-display font-semibold text-foreground mb-1">
                      Cara Pembelian Mounts
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Untuk membeli mount, silakan buka ticket di Discord server kami. Staff akan membantu proses pembelian kamu.
                    </p>
                    <Button
                      asChild
                      size="sm"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      <a
                        href={siteConfig.links.discord}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        Open Ticket di Discord
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </motion.div>

              {/* Footer Note */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-center mt-8 text-sm text-muted-foreground"
              >
                <p>
                  Semua mount bersifat permanen setelah dibeli.
                  <br />
                  Higher rarity mounts akan datang dengan updates berikutnya!
                </p>
              </motion.div>
            </>
          )}
        </Section>
      </main>
      <Footer />

      {/* Payment Modals */}
      <UsernameModal
        isOpen={showUsernameModal}
        onClose={handleCancelPayment}
        onSubmit={handleUsernameSubmit}
        productName={currentProduct?.name}
      />

      <RulesModal
        isOpen={showRulesModal}
        onClose={handleCancelPayment}
        onAccept={handleRulesAccept}
      />

      <ServerAlertModal
        isOpen={showServerAlert}
        onContinue={handleServerAlertContinue}
      />

      <MountAlertModal
        isOpen={showMountAlert}
        onClose={handleMountAlertContinue}
      />

      <PaymentQRModal
        isOpen={showPaymentQR}
        onClose={handleCancelPayment}
        orderData={orderData}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentFailed={handlePaymentFailed}
      />

      <ReceiptModal
        isOpen={showReceipt}
        onClose={handleReceiptClose}
        receiptData={receiptData}
      />
    </div>
  );
}
